import * as React from 'react';
import {FloatingOverlay} from '@floating-ui/react';
import Mask from '../../Mask';
import OverlayPortal from '../../OverlayPortal';
import {ScrubDirection} from '../_types';
import {SCRUB_DIRECTION} from '../_constants';
import {ScrubZoneOverlayView, ScrubZoneView} from './ScrubZone.style';

type ScrubZoneProps = {
  onScrub: (direction: ScrubDirection, isSkip: boolean) => void;
  setIsScrubbing: (isScrubbing: boolean) => void;
  isScrubbing?: boolean;
  children?: React.ReactNode;
};

// PIXELS_PER_STEP determines how many pixels the mouse must move horizontally before a value step is triggered.
const PIXELS_PER_STEP = 5; // Adjust for sensitivity: lower = more sensitive, higher = less

const ScrubZoneBase = ({
  onScrub,
  setIsScrubbing,
  isScrubbing = false,
  children,
}: ScrubZoneProps) => {
  const initialXRef = React.useRef<number | null>(null);
  const lastXRef = React.useRef<number | null>(null);
  const accumulatedDeltaRef = React.useRef(0);
  const [showOverlay, setShowOverlay] = React.useState(false);

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (initialXRef.current === null || lastXRef.current === null) return;
      e.preventDefault();
      e.stopPropagation();

      const deltaX = e.clientX - lastXRef.current;
      accumulatedDeltaRef.current += deltaX;

      // Only fire when the buffer exceeds the threshold
      while (Math.abs(accumulatedDeltaRef.current) >= PIXELS_PER_STEP) {
        const direction: ScrubDirection =
          accumulatedDeltaRef.current > 0
            ? SCRUB_DIRECTION.RIGHT
            : SCRUB_DIRECTION.LEFT;
        const isSkip = e.shiftKey;
        onScrub(direction, isSkip);

        // Remove one step's worth of movement in the direction scrubbed
        accumulatedDeltaRef.current -=
          PIXELS_PER_STEP * Math.sign(accumulatedDeltaRef.current);
      }

      lastXRef.current = e.clientX;
    },
    [onScrub]
  );

  const endScrubbing = React.useCallback(() => {
    initialXRef.current = null;
    lastXRef.current = null;
    accumulatedDeltaRef.current = 0;
    setIsScrubbing(false);
    setShowOverlay(false);
  }, [setIsScrubbing]);

  React.useEffect(() => {
    if (!showOverlay) return;
    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      endScrubbing();
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showOverlay, handleMouseMove, endScrubbing]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    initialXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    accumulatedDeltaRef.current = 0;
    setIsScrubbing(true);
    setShowOverlay(true);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endScrubbing);
  };

  return (
    <>
      <ScrubZoneView onMouseDown={handleMouseDown}>{children}</ScrubZoneView>
      {isScrubbing && (
        <OverlayPortal>
          <FloatingOverlay>
            <Mask
              shouldAllowPointerEvents={false}
              shouldEscapeViewport
              type="transparent"
            />
            <ScrubZoneOverlayView
              style={{width: window.innerWidth, height: window.innerHeight}}
            />
          </FloatingOverlay>
        </OverlayPortal>
      )}
    </>
  );
};

export const ScrubZone = React.memo(ScrubZoneBase);
